import type { TranslateMapConfig } from '@urc-ui/hooks/translate-map';
import { allCountries } from '@amzn/storm-ui-country-selector';

export const UNSUPPORTED_COUNTRIES_LIST: string[] = ['KR'];

/*
  The list of supported countries for the campaign country filter is based on Amazon DSP available
  Marketplaces (https://w.amazon.com/bin/view/ADSP_Global_Scale_%26_Localization_%28GSL%29/Where_is_the_DSP/).

  The list is sourced from https://code.amazon.com/packages/StormUI/blobs/4.x/--/packages/storm-ui-country-selector/src/constants/countryLists.ts
 */
export const SUPPORTED_COUNTRIES_LIST: string[] = allCountries.filter(country => !UNSUPPORTED_COUNTRIES_LIST.includes(country));

export enum SUPPORTED_COUNTRIES {
  AE = 'AE', // United Arab Emirates
  AR = 'AR', // Argentina
  AT = 'AT', // Austria
  AU = 'AU', // Australia
  BE = 'BE', // Belgium
  BG = 'BG', // Bulgaria
  BH = 'BH', // Bahrain
  BR = 'BR', // Brazil
  CA = 'CA', // Canada
  CH = 'CH', // Switzerland
  CL = 'CL', // Chile
  CN = 'CN', // China
  CO = 'CO', // Colombia
  CR = 'CR', // Costa Rica
  CY = 'CY', // Cyprus
  CZ = 'CZ', // Czech Republic
  DE = 'DE', // Germany
  DK = 'DK', // Denmark
  DO = 'DO', // Dominican Republic
  DZ = 'DZ', // Algeria
  EC = 'EC', // Ecuador
  EE = 'EE', // Estonia
  EG = 'EG', // Egypt
  ES = 'ES', // Spain
  FI = 'FI', // Finland
  FR = 'FR', // France
  GB = 'GB', // United Kingdom
  GR = 'GR', // Greece
  GT = 'GT', // Guatemala
  HK = 'HK', // Hong Kong
  HN = 'HN', // Honduras
  HR = 'HR', // Croatia
  HU = 'HU', // Hungary
  ID = 'ID', // Indonesia
  IE = 'IE', // Ireland
  IL = 'IL', // Israel
  IN = 'IN', // India
  IT = 'IT', // Italy
  JM = 'JM', // Jamaica'
  JP = 'JP', // Japan
  KW = 'KW', // Kuwait
  LT = 'LT', // Lithuania
  LU = 'LU', // Luxembourg
  LV = 'LV', // Latvia
  MA = 'MA', // Morocco
  MT = 'MT', // Malta
  MX = 'MX', // Mexico
  MY = 'MY', // Malaysia
  NL = 'NL', // Netherlands
  NO = 'NO', // Norway
  NZ = 'NZ', // New Zealand
  PA = 'PA', // Panama
  PE = 'PE', // Peru
  PH = 'PH', // Philippines
  PK = 'PK', // Pakistan
  PL = 'PL', // Poland
  PR = 'PR', // Puerto Rico
  PT = 'PT', // Portugal
  PY = 'PY', // Paraguay
  QA = 'QA', // Qatar
  RO = 'RO', // Romania
  SA = 'SA', // Saudia Arabia
  SE = 'SE', // Sweden
  SG = 'SG', // Singapore
  SK = 'SK', // Slovakia
  SV = 'SV', // El Salvador
  TH = 'TH', // Thailand
  TN = 'TN', // Tunisia
  TR = 'TR', // Turkey
  TW = 'TW', // Taiwan
  UK = 'UK', // United Kingdom
  US = 'US', // United States
  UY = 'UY', // Uruguay
  VN = 'VN', // Vietnam
  ZA = 'ZA' // South Africa
}

export const SUPPORTED_COUNTRIES_TRANSLATION_MAP: TranslateMapConfig = {
  [SUPPORTED_COUNTRIES.AE]: {
    id: 'sspa_rcm_ad_country_ae',
    default: 'United Arab Emirates'
  },
  [SUPPORTED_COUNTRIES.AR]: {
    id: 'flexible_reports_country_ar',
    default: 'Argentina'
  },
  [SUPPORTED_COUNTRIES.AT]: {
    id: 'flexible_reports_country_at',
    default: 'Austria'
  },
  [SUPPORTED_COUNTRIES.AU]: {
    id: 'sspa_rcm_ad_country_au',
    default: 'Australia'
  },
  [SUPPORTED_COUNTRIES.BE]: {
    id: 'sspa_rcm_ad_country_be',
    default: 'Belgium'
  },
  [SUPPORTED_COUNTRIES.BG]: {
    id: 'flexible_reports_country_bg',
    default: 'Bulgaria'
  },
  [SUPPORTED_COUNTRIES.BH]: {
    id: 'flexible_reports_country_bh',
    default: 'Bahrain'
  },
  [SUPPORTED_COUNTRIES.BR]: {
    id: 'sspa_rcm_ad_country_br',
    default: 'Brazil'
  },
  [SUPPORTED_COUNTRIES.CA]: {
    id: 'sspa_rcm_ad_country_ca',
    default: 'Canada'
  },
  [SUPPORTED_COUNTRIES.CH]: {
    id: 'flexible_reports_country_ch',
    default: 'Switzerland'
  },
  [SUPPORTED_COUNTRIES.CL]: {
    id: 'sspa_rcm_ad_country_cl',
    default: 'Chile'
  },
  [SUPPORTED_COUNTRIES.CN]: {
    id: 'flexible_reports_country_cn',
    default: 'China'
  },
  [SUPPORTED_COUNTRIES.CO]: {
    id: 'sspa_rcm_ad_country_co',
    default: 'Colombia'
  },
  [SUPPORTED_COUNTRIES.CR]: {
    id: 'flexible_reports_country_cr',
    default: 'Costa Rica'
  },
  [SUPPORTED_COUNTRIES.CY]: {
    id: 'flexible_reports_country_cy',
    default: 'Cyprus'
  },
  [SUPPORTED_COUNTRIES.CZ]: {
    id: 'flexible_reports_country_cz',
    default: 'Czech Republic'
  },
  [SUPPORTED_COUNTRIES.DE]: {
    id: 'sspa_rcm_ad_country_de',
    default: 'Germany'
  },
  [SUPPORTED_COUNTRIES.DK]: {
    id: 'flexible_reports_country_dk',
    default: 'Denmark'
  },
  [SUPPORTED_COUNTRIES.DO]: {
    id: 'flexible_reports_country_do',
    default: 'Dominican Republic'
  },
  [SUPPORTED_COUNTRIES.DZ]: {
    id: 'flexible_reports_country_dz',
    default: 'Algeria'
  },
  [SUPPORTED_COUNTRIES.EC]: {
    id: 'flexible_reports_country_ec',
    default: 'Ecuador'
  },
  [SUPPORTED_COUNTRIES.EE]: {
    id: 'flexible_reports_country_ee',
    default: 'Estonia'
  },
  [SUPPORTED_COUNTRIES.EG]: {
    id: 'sspa_rcm_ad_country_eg',
    default: 'Egypt'
  },
  [SUPPORTED_COUNTRIES.ES]: {
    id: 'sspa_rcm_ad_country_es',
    default: 'Spain'
  },
  [SUPPORTED_COUNTRIES.FI]: {
    id: 'flexible_reports_country_fi',
    default: 'Finland'
  },
  [SUPPORTED_COUNTRIES.FR]: {
    id: 'sspa_rcm_ad_country_fr',
    default: 'France'
  },
  [SUPPORTED_COUNTRIES.GB]: {
    id: 'sspa_rcm_ad_country_uk',
    default: 'United Kingdom'
  },
  [SUPPORTED_COUNTRIES.GR]: {
    id: 'flexible_reports_country_gr',
    default: 'Greece'
  },
  [SUPPORTED_COUNTRIES.GT]: {
    id: 'flexible_reports_country_gt',
    default: 'Guatemala'
  },
  [SUPPORTED_COUNTRIES.HK]: {
    id: 'flexible_reports_country_hk',
    default: 'Hong Kong'
  },
  [SUPPORTED_COUNTRIES.HN]: {
    id: 'flexible_reports_country_hn',
    default: 'Honduras'
  },
  [SUPPORTED_COUNTRIES.HR]: {
    id: 'flexible_reports_country_hr',
    default: 'Croatia'
  },
  [SUPPORTED_COUNTRIES.HU]: {
    id: 'flexible_reports_country_hu',
    default: 'Hungary'
  },
  [SUPPORTED_COUNTRIES.ID]: {
    id: 'flexible_reports_country_id',
    default: 'Indonesia'
  },
  [SUPPORTED_COUNTRIES.IE]: {
    id: 'sspa_rcm_ad_country_ie',
    default: 'Ireland'
  },
  [SUPPORTED_COUNTRIES.IL]: {
    id: 'flexible_reports_country_il',
    default: 'Israel'
  },
  [SUPPORTED_COUNTRIES.IN]: {
    id: 'sspa_rcm_ad_country_in',
    default: 'India'
  },
  [SUPPORTED_COUNTRIES.IT]: {
    id: 'sspa_rcm_ad_country_it',
    default: 'Italy'
  },
  [SUPPORTED_COUNTRIES.JM]: {
    id: 'flexible_reports_country_jm',
    default: 'Jamaica'
  },
  [SUPPORTED_COUNTRIES.JP]: {
    id: 'sspa_rcm_ad_country_jp',
    default: 'Japan'
  },
  [SUPPORTED_COUNTRIES.KW]: {
    id: 'flexible_reports_country_kw',
    default: 'Kuwait'
  },
  [SUPPORTED_COUNTRIES.LT]: {
    id: 'flexible_reports_country_lt',
    default: 'Lithuania'
  },
  [SUPPORTED_COUNTRIES.LU]: {
    id: 'flexible_reports_country_lu',
    default: 'Luxembourg'
  },
  [SUPPORTED_COUNTRIES.LV]: {
    id: 'flexible_reports_country_lv',
    default: 'Latvia'
  },
  [SUPPORTED_COUNTRIES.MA]: {
    id: 'flexible_reports_country_ma',
    default: 'Morocco'
  },
  [SUPPORTED_COUNTRIES.MT]: {
    id: 'flexible_reports_country_mt',
    default: 'Malta'
  },
  [SUPPORTED_COUNTRIES.MX]: {
    id: 'sspa_rcm_ad_country_mx',
    default: 'Mexico'
  },
  [SUPPORTED_COUNTRIES.MY]: {
    id: 'flexible_reports_country_my',
    default: 'Malaysia'
  },
  [SUPPORTED_COUNTRIES.NL]: {
    id: 'sspa_rcm_ad_country_nl',
    default: 'Netherlands'
  },
  [SUPPORTED_COUNTRIES.NO]: {
    id: 'flexible_reports_country_no',
    default: 'Norway'
  },
  [SUPPORTED_COUNTRIES.NZ]: {
    id: 'flexible_reports_country_nz',
    default: 'New Zealand'
  },
  [SUPPORTED_COUNTRIES.PA]: {
    id: 'flexible_reports_country_pa',
    default: 'Panama'
  },
  [SUPPORTED_COUNTRIES.PE]: {
    id: 'flexible_reports_country_pe',
    default: 'Peru'
  },
  [SUPPORTED_COUNTRIES.PH]: {
    id: 'flexible_reports_country_ph',
    default: 'Philippines'
  },
  [SUPPORTED_COUNTRIES.PK]: {
    id: 'flexible_reports_country_pk',
    default: 'Pakistan'
  },
  [SUPPORTED_COUNTRIES.PL]: {
    id: 'sspa_rcm_ad_country_pl',
    default: 'Poland'
  },
  [SUPPORTED_COUNTRIES.PR]: {
    id: 'flexible_reports_country_pr',
    default: 'Puerto Rico'
  },
  [SUPPORTED_COUNTRIES.PT]: {
    id: 'flexible_reports_country_pt',
    default: 'Portugal'
  },
  [SUPPORTED_COUNTRIES.PY]: {
    id: 'flexible_reports_country_py',
    default: 'Paraguay'
  },
  [SUPPORTED_COUNTRIES.QA]: {
    id: 'flexible_reports_country_qa',
    default: 'Qatar'
  },
  [SUPPORTED_COUNTRIES.RO]: {
    id: 'flexible_reports_country_ro',
    default: 'Romania'
  },
  [SUPPORTED_COUNTRIES.SA]: {
    id: 'sspa_rcm_ad_country_sa',
    default: 'Saudi Arabia'
  },
  [SUPPORTED_COUNTRIES.SE]: {
    id: 'sspa_rcm_ad_country_se',
    default: 'Sweden'
  },
  [SUPPORTED_COUNTRIES.SG]: {
    id: 'sspa_rcm_ad_country_sg',
    default: 'Singapore'
  },
  [SUPPORTED_COUNTRIES.SK]: {
    id: 'flexible_reports_country_sk',
    default: 'Slovakia'
  },
  [SUPPORTED_COUNTRIES.SV]: {
    id: 'flexible_reports_country_sv',
    default: 'El Salvador'
  },
  [SUPPORTED_COUNTRIES.TH]: {
    id: 'flexible_reports_country_th',
    default: 'Thailand'
  },
  [SUPPORTED_COUNTRIES.TN]: {
    id: 'flexible_reports_country_tn',
    default: 'Tunisia'
  },
  [SUPPORTED_COUNTRIES.TR]: {
    id: 'sspa_rcm_ad_country_tr',
    default: 'Turkey'
  },
  [SUPPORTED_COUNTRIES.TW]: {
    id: 'flexible_reports_country_tw',
    default: 'Taiwan'
  },
  [SUPPORTED_COUNTRIES.UK]: {
    id: 'sspa_rcm_ad_country_uk',
    default: 'United Kingdom'
  },
  [SUPPORTED_COUNTRIES.US]: {
    id: 'sspa_rcm_ad_country_us',
    default: 'United States'
  },
  [SUPPORTED_COUNTRIES.UY]: {
    id: 'flexible_reports_country_uy',
    default: 'Uruguay'
  },
  [SUPPORTED_COUNTRIES.VN]: {
    id: 'flexible_reports_country_vn',
    default: 'Vietnam'
  },
  [SUPPORTED_COUNTRIES.ZA]: {
    id: 'sspa_rcm_ad_country_za',
    default: 'South Africa'
  }
};

const COUNTRY_CODE_MAPPINGS: { [key: string]: string } = {
  GB: 'UK'
};

/*
  Depending on the country, the same country code used for the StormUI component may not be the same that needs to be
  used for the api request, having different country codes for the same country. This method is used to track and include both.

  @param countries - The list of countries selected by the user from the UI component
  @returns The list of country codes that need to be used for the api request
 */
export const determineCountryCodesForRequest = (countries: string[]): string[] => {
  return countries.map(country => COUNTRY_CODE_MAPPINGS[country] || country);
};

/* Map the API values back to storm supported country codes */
export const determineCountryCodesFromResponse = (countries: string[]): string[] => {
  const reverseMap = Object.entries(COUNTRY_CODE_MAPPINGS).reduce((acc, [key, value]) => {
    acc[value] = key;
    return acc;
  }, {} as Record<string, string>);

  return countries.map(country => reverseMap[country] || country);
};
